import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import specialitiesData from '../../../data/specialities.json';
import genderData from '../../../data/genderOptions.json';
import languageData from '../../../data/languageOptions.json';
import {
  API_DOCTOR_CREATE,
  API_DOCTOR_UPDATE,
} from '../../../helpers/urlHelper';
import { uploadSingleFile, uploadMultipleFiles } from '../../../helpers/uploadHelper';

const DoctorSave = ({ show, handleClose, isEdit, doctorData }) => {
  const [formData, setFormData] = useState({
    profilePicture: null,
    name: '',
    surname: '',
    phone: '',
    email: '',
    license: '',
    gender: '',
    experienceInYears: '',
    fees: '',
    languages: [],
    services: [],
    position: '',
    degree: '',
    role: 'doctor',
    image: null,
    certificates: [],
    uploads: [],
  });

  useEffect(() => {
    if (isEdit && doctorData) {
      setFormData({
        profilePicture: null,
        image: doctorData.image || null,
        name: doctorData.name || '',
        surname: doctorData.surname || '',
        phone: doctorData.phone || '',
        email: doctorData.email || '',
        license: doctorData.license || '',
        gender: doctorData.gender || '',
        experienceInYears: doctorData.experienceInYears || '',
        fees: doctorData.fees || '',
        languages: doctorData.languages || [],
        services: doctorData.services || [],
        position: doctorData.position || '',
        degree: doctorData.degree || '',
        role: 'doctor',
        certificates: [],
        uploads: doctorData.uploads || [],
      });
    } else {
      setFormData({
        profilePicture: null,
        image: null,
        name: '',
        surname: '',
        phone: '',
        email: '',
        license: '',
        gender: '',
        experienceInYears: '',
        fees: '',
        languages: [],
        services: [],
        position: '',
        degree: '',
        role: 'doctor',
        certificates: [],
        uploads: [],
      });
    }
  }, [isEdit, doctorData]);

  const handleTextInput = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfilePicture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const uploaded = await uploadSingleFile(file);
      setFormData(prev => ({
        ...prev,
        profilePicture: file,
        image: uploaded._id,
      }));
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      alert('Error subiendo la foto de perfil');
    }
  };

  const handleCertificatesChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    try {
      const uploaded = await uploadMultipleFiles(files);
      setFormData(prev => ({
        ...prev,
        certificates: files,
        uploads: [...prev.uploads, ...uploaded.map(f => f._id)],
      }));
    } catch (err) {
      console.error('Error uploading certificates:', err);
      alert('Error subiendo los certificados');
    }
  };

  const handleGenderSelect = option => {
    setFormData(prev => ({ ...prev, gender: option?.value || '' }));
  };

  const handleLanguagesSelect = options => {
    setFormData(prev => ({
      ...prev,
      languages: options ? options.map(o => o.value) : [],
    }));
  };

  const handleServicesSelect = options => {
    setFormData(prev => ({
      ...prev,
      services: options ? options.map(o => o.value) : [],
    }));
  };

  const getCurrentValue = (field, options) => {
    if (field === 'gender') {
      return options.find(o => o.value === formData[field]) || null;
    }
    return formData[field].map(slug =>
        options.find(o => o.value === slug)
    ).filter(Boolean);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = isEdit
          ? API_DOCTOR_UPDATE(doctorData._id)
          : API_DOCTOR_CREATE;
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || res.statusText);
      }
      await res.json();
      handleClose();
    } catch (err) {
      console.error('Error saving doctor:', err);
      alert(`No se pudo ${isEdit ? 'actualizar' : 'crear'} el doctor: ${err.message}`);
    }
  };

  return (
      <div className={`offcanvas offcanvas-end ${show ? 'show' : ''}`} tabIndex="-1" style={{ minWidth: '800px' }}>
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">{isEdit ? 'Edit Doctor' : 'Add New Doctor'}</h5>
          <button type="button" className="btn-close" onClick={handleClose}></button>
        </div>
        <div className="offcanvas-body">
          <form>
            <div className="mb-3">
              <label className="form-label">Profile Picture</label>
              <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={handleProfilePicture}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                  type="text"
                  className="form-control"
                  name="name"
                  value={formData.name}
                  onChange={handleTextInput}
                  required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Surname</label>
              <input
                  type="text"
                  className="form-control"
                  name="surname"
                  value={formData.surname}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                  type="tel"
                  className="form-control"
                  name="phone"
                  value={formData.phone}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Email ID</label>
              <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={formData.email}
                  onChange={handleTextInput}
                  required
              />
            </div>

            <div className="mb-3">
              <label className="form-label">License Number</label>
              <input
                  type="text"
                  className="form-control"
                  name="license"
                  value={formData.license}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Gender</label>
              <Select
                  name="gender"
                  options={genderData.genderOptions}
                  value={getCurrentValue('gender', genderData.genderOptions)}
                  onChange={handleGenderSelect}
                  isClearable
                  className="react-select"
                  classNamePrefix="select"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Years of Experience</label>
              <input
                  type="number"
                  className="form-control"
                  name="experienceInYears"
                  value={formData.experienceInYears}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Fees</label>
              <input
                  type="number"
                  className="form-control"
                  name="fees"
                  value={formData.fees}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Position</label>
              <input
                  type="text"
                  className="form-control"
                  name="position"
                  value={formData.position}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Degree</label>
              <input
                  type="text"
                  className="form-control"
                  name="degree"
                  value={formData.degree}
                  onChange={handleTextInput}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Languages</label>
              <Select
                  name="languages"
                  options={languageData.languageOptions}
                  value={languageData.languageOptions.filter(lang =>
                      formData.languages.includes(lang.value)
                  )}
                  onChange={handleLanguagesSelect}
                  isMulti
                  className="react-select"
                  classNamePrefix="select"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Specialities</label>
              <Select
                  name="services"
                  options={specialitiesData.specialities.map(spec => ({
                    value: spec.slug,
                    label: spec.value
                  }))}
                  value={specialitiesData.specialities
                      .filter(spec => formData.services.includes(spec.slug))
                      .map(spec => ({
                        value: spec.slug,
                        label: spec.value
                      }))}
                  onChange={handleServicesSelect}
                  isMulti
                  className="react-select"
                  classNamePrefix="select"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Certificates</label>
              <input
                  type="file"
                  className="form-control"
                  name="certificates"
                  multiple
                  onChange={handleCertificatesChange}
              />
              {formData.certificates.length > 0 && (
                  <div className="mt-2">
                    <p>Selected files:</p>
                    <ul className="list-unstyled">
                      {formData.certificates.map((file, index) => (
                          <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </div>
              )}
            </div>
          </form>
        </div>

        <div className="offcanvas-footer p-3 border-top">
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-secondary" onClick={handleClose}>
              Close
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSubmit}>
              {isEdit ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
  );
};

export default DoctorSave;
